import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@material-ui/core';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { AwsAccountIdInput } from './AwsAccountIdInput';
import {
  InstanceSelectionTable,
  osMatches,
  type InstanceCandidate,
} from './InstanceSelectionTable';

type Provider = {
  id: string;
  title: string;
  availability: 'available' | 'coming-soon' | 'not-configured';
  reason?: string;
};
type Account = {
  id: string;
  kind: 'runtime' | 'assume-role';
  accountId: string;
  status: string;
  error?: { code: string; message: string; permission?: string };
  permissionChecks?: { permission: string; status: string }[];
  canManage: boolean;
};
type Region = { id: string; name: string };
type Network = {
  id: string;
  name?: string;
  isDefault: boolean;
  cidr?: string;
  subnets: { id: string; name?: string }[];
};
type Resource = {
  id: string;
  name?: string;
  state?: string;
  instanceType?: string;
  subnetId?: string;
  availabilityZone?: string;
  ssmStatus?: string;
  architecture?: string;
  launchImageId?: string;
  sourceOs?: InstanceCandidate['sourceOs'];
};
type ScopeView = {
  scopeId: string;
  resourceCount: number;
  children?: { resourceRef: string; displayName?: string }[];
  hierarchy: {
    accountId: string;
    connectionId: string;
    regions: {
      region: string;
      networks: {
        networkId: string;
        name?: string;
        subnets: { subnetId: string | null; resourceRefs: string[] }[];
      }[];
    }[];
  }[];
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
};

const defaultProviders: Provider[] = [
  { id: 'aws', title: 'Amazon Web Services (AWS)', availability: 'available' },
  { id: 'azure', title: 'Microsoft Azure', availability: 'coming-soon' },
  { id: 'gcp', title: 'Google Cloud', availability: 'coming-soon' },
  {
    id: 'oci',
    title: 'Oracle Cloud Infrastructure',
    availability: 'coming-soon',
  },
];

const discoveryConcurrency = 3;

async function forEachDiscovery<T>(
  items: T[],
  isCurrent: () => boolean,
  visit: (item: T) => Promise<void>,
) {
  let next = 0;
  await Promise.all(
    Array.from(
      { length: Math.min(discoveryConcurrency, items.length) },
      async () => {
        while (isCurrent()) {
          const index = next++;
          if (index >= items.length) return;
          await visit(items[index]);
        }
      },
    ),
  );
}

function object(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function errorMessage(caught: unknown) {
  return caught instanceof Error ? caught.message : 'Request failed';
}

function accountRegionKey(accountId: string, region: string) {
  return `${accountId}/${region}`;
}

function networkKey(accountId: string, region: string, networkId: string) {
  return `${accountId}/${region}/${networkId}`;
}

function newIdempotencyKey() {
  const uuid = globalThis.crypto?.randomUUID?.();
  return `cloud-scope-${
    uuid ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`
  }`;
}

async function readJson(response: Response): Promise<unknown> {
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = (await response.json()) as {
        error?: { message?: string };
        message?: string;
      };
      message = body.error?.message ?? body.message ?? message;
    } catch {
      // An intermediary may send a non-JSON error response.
    }
    throw new Error(message);
  }
  return response.json();
}

function parseProviders(value: unknown): Provider[] {
  const raw = object(value) ? value.providers : undefined;
  if (!Array.isArray(raw)) throw new Error('Invalid cloud provider response');
  const providers = raw.filter(
    (item): item is Provider =>
      object(item) &&
      typeof item.id === 'string' &&
      typeof item.title === 'string' &&
      ['available', 'coming-soon', 'not-configured'].includes(
        String(item.availability),
      ),
  );
  if (providers.length !== raw.length) {
    throw new Error('Invalid cloud provider response');
  }
  return defaultProviders.map(
    fallback =>
      providers.find(provider => provider.id === fallback.id) ?? fallback,
  );
}

function parseAccounts(value: unknown) {
  if (!object(value) || !Array.isArray(value.accounts)) {
    throw new Error('Invalid cloud account response');
  }
  const accounts = value.accounts.map(parseAccount);
  return { accounts, canManage: value.canManage === true };
}

function parseAccount(value: unknown): Account {
  const candidate =
    object(value) && object(value.account) ? value.account : value;
  if (
    !object(candidate) ||
    typeof candidate.id !== 'string' ||
    typeof candidate.accountId !== 'string' ||
    !/^\d{12}$/.test(candidate.accountId) ||
    typeof candidate.status !== 'string'
  ) {
    throw new Error('Invalid cloud account response');
  }
  return {
    id: candidate.id,
    accountId: candidate.accountId,
    kind: candidate.kind === 'runtime' ? 'runtime' : 'assume-role',
    status: candidate.status,
    canManage: candidate.canManage === true,
    error:
      object(candidate.error) && typeof candidate.error.message === 'string'
        ? {
            code:
              typeof candidate.error.code === 'string'
                ? candidate.error.code
                : 'UNKNOWN',
            message: candidate.error.message,
            permission:
              typeof candidate.error.permission === 'string'
                ? candidate.error.permission
                : undefined,
          }
        : undefined,
    permissionChecks: Array.isArray(candidate.permissionChecks)
      ? candidate.permissionChecks.filter(
          (item): item is { permission: string; status: string } =>
            object(item) &&
            typeof item.permission === 'string' &&
            typeof item.status === 'string',
        )
      : [],
  };
}

function parseScope(value: unknown): ScopeView {
  const candidate = object(value) && object(value.scope) ? value.scope : value;
  const hierarchy = object(candidate) ? candidate.hierarchy : undefined;
  const validHierarchy =
    Array.isArray(hierarchy) &&
    hierarchy.every(
      account =>
        object(account) &&
        typeof account.accountId === 'string' &&
        typeof account.connectionId === 'string' &&
        Array.isArray(account.regions) &&
        account.regions.every(
          region =>
            object(region) &&
            typeof region.region === 'string' &&
            Array.isArray(region.networks) &&
            region.networks.every(
              network =>
                object(network) &&
                typeof network.networkId === 'string' &&
                Array.isArray(network.subnets) &&
                network.subnets.every(
                  subnet =>
                    object(subnet) &&
                    (subnet.subnetId === null ||
                      typeof subnet.subnetId === 'string') &&
                    Array.isArray(subnet.resourceRefs) &&
                    subnet.resourceRefs.every(ref => typeof ref === 'string'),
                ),
            ),
        ),
    );
  if (
    !object(candidate) ||
    typeof candidate.scopeId !== 'string' ||
    !/^asscope-[A-Za-z0-9-]+$/.test(candidate.scopeId) ||
    typeof candidate.resourceCount !== 'number' ||
    !validHierarchy
  ) {
    throw new Error('Invalid frozen assessment scope response');
  }
  return candidate as ScopeView;
}

function SelectionCheck(props: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <input
        type="checkbox"
        checked={props.checked}
        disabled={props.disabled}
        onChange={event => props.onChange(event.target.checked)}
      />
      <span>{props.label}</span>
    </label>
  );
}

/** Browser state is a preview only; the ASSESS API re-discovers and freezes scope. */
export function HorizonCloudScopePicker({ value, onChange, disabled }: Props) {
  const fieldId = useId();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const requestVersion = useRef(0);
  const detectStarted = useRef(false);
  const idempotencyKey = useRef(newIdempotencyKey());
  const supersedesScopeId = useRef<string>();
  const [providers, setProviders] = useState(defaultProviders);
  const [provider, setProvider] = useState(
    value.startsWith('asscope-') ? 'aws' : '',
  );
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [canManage, setCanManage] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [regions, setRegions] = useState<Record<string, Region[]>>({});
  const [regionModes, setRegionModes] = useState<
    Record<string, 'all' | 'selected'>
  >({});
  const [selectedRegions, setSelectedRegions] = useState<
    Record<string, string[]>
  >({});
  const [networks, setNetworks] = useState<Record<string, Network[]>>({});
  const [selectedNetworks, setSelectedNetworks] = useState<
    Record<string, string[]>
  >({});
  const [resources, setResources] = useState<Record<string, Resource[]>>({});
  const [selectedInstances, setSelectedInstances] = useState<string[]>([]);
  const [selectedDetails, setSelectedDetails] = useState<InstanceCandidate[]>(
    [],
  );
  const [osView, setOsView] = useState('');
  const [selectionNotice, setSelectionNotice] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState('');
  const [networkSearch, setNetworkSearch] = useState('');
  const [review, setReview] = useState(false);
  const [frozen, setFrozen] = useState<ScopeView | null>(null);
  const [confirmed, setConfirmed] = useState(value.startsWith('asscope-'));
  const [restoreAttempt, setRestoreAttempt] = useState(0);

  const request = useCallback(
    async (path: string, init?: RequestInit) => {
      const base = await discoveryApi.getBaseUrl('horizon-assess');
      return readJson(await fetchApi.fetch(`${base}/v1/cloud${path}`, init));
    },
    [discoveryApi, fetchApi],
  );

  const setError = (key: string, message: string) => {
    setErrors(previous => ({ ...previous, [key]: message }));
  };
  const clearError = (key: string) => {
    setErrors(previous => {
      const next = { ...previous };
      delete next[key];
      return next;
    });
  };
  const invalidate = () => {
    if (frozen?.scopeId) supersedesScopeId.current = frozen.scopeId;
    if (selectedInstances.length)
      setSelectionNotice(
        'Explicit instance selections are preserved. Reconcile any selections outside the current account/Region/VPC scope before freeze.',
      );
    requestVersion.current += 1;
    idempotencyKey.current = newIdempotencyKey();
    setFrozen(null);
    setConfirmed(false);
    setReview(false);
    setResources({});
    setErrors(previous =>
      Object.fromEntries(
        Object.entries(previous).filter(
          ([key]) =>
            key !== 'freeze' &&
            !key.startsWith('network:') &&
            !key.startsWith('resource:'),
        ),
      ),
    );
    if (value) onChange('');
  };

  const loadAccounts = useCallback(async () => {
    const parsed = parseAccounts(await request('/accounts'));
    setAccounts(parsed.accounts);
    setCanManage(parsed.canManage);
    return parsed;
  }, [request]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setBusy('initial');
      try {
        const result = parseProviders(await request('/providers'));
        if (active) setProviders(result);
      } catch (caught) {
        if (active) setError('providers', errorMessage(caught));
      }
      try {
        if (active) await loadAccounts();
      } catch (caught) {
        if (active) setError('accounts', errorMessage(caught));
      } finally {
        if (active) setBusy('');
      }
    };
    void load();
    return () => {
      active = false;
      requestVersion.current += 1;
    };
  }, [loadAccounts, request]);

  useEffect(() => {
    if (!value.startsWith('asscope-')) return;
    const version = ++requestVersion.current;
    setErrors(previous => {
      const next = { ...previous };
      delete next.restore;
      return next;
    });
    setProvider('aws');
    setBusy('restore');
    void request(`/scopes/${encodeURIComponent(value)}`)
      .then(response => {
        if (version === requestVersion.current) setFrozen(parseScope(response));
      })
      .catch(caught => {
        if (version === requestVersion.current) {
          setError('restore', errorMessage(caught));
        }
      })
      .finally(() => {
        if (version === requestVersion.current) setBusy('');
      });
  }, [request, restoreAttempt, value]);

  const detectRuntime = async () => {
    setBusy('detect');
    try {
      await request('/accounts/detect', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: '{}',
      });
      await loadAccounts();
      clearError('detect');
    } catch (caught) {
      setError('detect', errorMessage(caught));
    } finally {
      setBusy('');
    }
  };

  const chooseProvider = async (next: string) => {
    invalidate();
    setProvider(next);
    setSelectedAccounts([]);
    setRegions({});
    setRegionModes({});
    setSelectedRegions({});
    setNetworks({});
    setSelectedNetworks({});
    if (next !== 'aws' || detectStarted.current) return;
    detectStarted.current = true;
    await detectRuntime();
  };

  const manageAccount = async (
    path: string,
    method: 'POST' | 'DELETE',
    body?: Record<string, string>,
  ) => {
    setBusy('manage');
    clearError('manage');
    try {
      const init: RequestInit = { method };
      if (body || method === 'POST') {
        init.headers = { 'content-type': 'application/json' };
        init.body = JSON.stringify(body ?? {});
      }
      await request(path, init);
      invalidate();
      setSelectedAccounts([]);
      setRegions({});
      setRegionModes({});
      setSelectedRegions({});
      setNetworks({});
      setSelectedNetworks({});
      await loadAccounts();
      return true;
    } catch (caught) {
      setError('manage', errorMessage(caught));
      return false;
    } finally {
      setBusy('');
    }
  };

  const toggleAccount = async (accountId: string, checked: boolean) => {
    if (
      checked &&
      accounts.find(account => account.id === accountId)?.status !== 'CONNECTED'
    ) {
      return;
    }
    invalidate();
    setNetworks({});
    setSelectedNetworks({});
    setSelectedRegions({});
    setRegionModes({});
    setSelectedAccounts(previous => {
      if (!checked) return previous.filter(id => id !== accountId);
      return previous.includes(accountId) ? previous : [...previous, accountId];
    });
    if (!checked || regions[accountId]) return;
    const version = requestVersion.current;
    setBusy(`regions:${accountId}`);
    try {
      const result = await request(
        `/accounts/${encodeURIComponent(accountId)}/regions`,
      );
      const rawRegions = object(result) ? result.regions : undefined;
      if (!Array.isArray(rawRegions)) {
        throw new Error('Invalid cloud regions response');
      }
      const parsedRegions = rawRegions.filter(
        (item): item is Region =>
          object(item) &&
          typeof item.id === 'string' &&
          typeof item.name === 'string',
      );
      if (parsedRegions.length !== rawRegions.length) {
        throw new Error('Invalid cloud regions response');
      }
      if (version !== requestVersion.current) return;
      setRegions(previous => ({
        ...previous,
        [accountId]: parsedRegions,
      }));
      setRegionModes(previous => ({ ...previous, [accountId]: 'all' }));
      clearError(`regions:${accountId}`);
    } catch (caught) {
      if (version === requestVersion.current) {
        setError(`regions:${accountId}`, errorMessage(caught));
      }
    } finally {
      if (version === requestVersion.current) setBusy('');
    }
  };

  const selectedRegionIds = (accountId: string) =>
    regionModes[accountId] === 'selected'
      ? selectedRegions[accountId] ?? []
      : (regions[accountId] ?? []).map(region => region.id);

  const discoverNetworks = async () => {
    invalidate();
    setNetworks({});
    setSelectedNetworks({});
    const version = requestVersion.current;
    const pairs = selectedAccounts.flatMap(accountId =>
      selectedRegionIds(accountId).map(region => ({ accountId, region })),
    );
    if (pairs.length === 0) {
      setError('networks', 'Select at least one enabled Region.');
      return;
    }
    clearError('networks');
    setBusy('networks');
    await forEachDiscovery(
      pairs,
      () => version === requestVersion.current,
      async ({ accountId, region }) => {
        const key = accountRegionKey(accountId, region);
        try {
          const result = await request(
            `/accounts/${encodeURIComponent(
              accountId,
            )}/regions/${encodeURIComponent(region)}/networks`,
          );
          const rawNetworks = object(result) ? result.networks : undefined;
          if (!Array.isArray(rawNetworks)) {
            throw new Error('Invalid cloud networks response');
          }
          const parsedNetworks = rawNetworks.filter(
            (item): item is Network =>
              object(item) &&
              typeof item.id === 'string' &&
              typeof item.isDefault === 'boolean' &&
              Array.isArray(item.subnets),
          );
          if (parsedNetworks.length !== rawNetworks.length) {
            throw new Error('Invalid cloud networks response');
          }
          if (version !== requestVersion.current) return;
          setNetworks(previous => ({
            ...previous,
            [key]: parsedNetworks,
          }));
          clearError(`network:${key}`);
        } catch (caught) {
          if (version === requestVersion.current) {
            setError(`network:${key}`, errorMessage(caught));
          }
        }
      },
    );
    if (version === requestVersion.current) setBusy('');
  };

  const discoverResources = async () => {
    invalidate();
    const version = requestVersion.current;
    const choices = Object.entries(selectedNetworks).flatMap(([pair, ids]) => {
      const [accountId, region] = pair.split('/');
      return ids.map(networkId => ({ accountId, region, networkId }));
    });
    if (choices.length === 0) {
      setError('resources', 'Select at least one VPC.');
      return;
    }
    setBusy('resources');
    await forEachDiscovery(
      choices,
      () => version === requestVersion.current,
      async ({ accountId, region, networkId }) => {
        const key = networkKey(accountId, region, networkId);
        try {
          const result = await request(
            `/accounts/${encodeURIComponent(
              accountId,
            )}/regions/${encodeURIComponent(
              region,
            )}/networks/${encodeURIComponent(networkId)}/resources`,
          );
          const rawResources = object(result) ? result.resources : undefined;
          const expectedAccount = accounts.find(
            item => item.id === accountId,
          )?.accountId;
          if (
            object(result) &&
            ((result.accountId !== undefined &&
              result.accountId !== expectedAccount) ||
              (result.connectionId !== undefined &&
                result.connectionId !== accountId) ||
              (result.region !== undefined && result.region !== region) ||
              (result.vpcId !== undefined && result.vpcId !== networkId))
          ) {
            throw new Error(
              'Resource response does not match the selected account/Region/VPC context',
            );
          }
          if (!Array.isArray(rawResources)) {
            throw new Error('Invalid cloud resources response');
          }
          const parsedResources = rawResources.filter(
            (item): item is Resource =>
              object(item) &&
              typeof item.id === 'string' &&
              (item.accountId === undefined ||
                item.accountId === expectedAccount) &&
              (item.region === undefined || item.region === region) &&
              (item.vpcId === undefined || item.vpcId === networkId),
          );
          if (parsedResources.length !== rawResources.length) {
            throw new Error('Invalid cloud resources response');
          }
          if (version !== requestVersion.current) return;
          setResources(previous => ({
            ...previous,
            [key]: parsedResources,
          }));
          clearError(`resource:${key}`);
        } catch (caught) {
          if (version === requestVersion.current) {
            setError(`resource:${key}`, errorMessage(caught));
          }
        }
      },
    );
    if (version === requestVersion.current) setBusy('');
  };

  const selectedPairs = selectedAccounts.flatMap(accountId =>
    selectedRegionIds(accountId).map(region => ({ accountId, region })),
  );
  const allAccountsConnected = selectedAccounts.every(
    id => accounts.find(account => account.id === id)?.status === 'CONNECTED',
  );
  const selectedVpcChoices = selectedPairs.flatMap(({ accountId, region }) =>
    (selectedNetworks[accountRegionKey(accountId, region)] ?? []).map(
      networkId => ({ accountId, region, networkId }),
    ),
  );
  const allNetworksLoaded =
    selectedPairs.length > 0 &&
    selectedPairs.every(
      ({ accountId, region }) =>
        networks[accountRegionKey(accountId, region)] !== undefined,
    );
  const allResourcesLoaded =
    selectedVpcChoices.length > 0 &&
    selectedVpcChoices.every(
      ({ accountId, region, networkId }) =>
        resources[networkKey(accountId, region, networkId)] !== undefined,
    );
  const resourceCount = selectedVpcChoices.reduce(
    (count, { accountId, region, networkId }) =>
      count +
      (resources[networkKey(accountId, region, networkId)]?.length ?? 0),
    0,
  );

  const candidates: InstanceCandidate[] = selectedVpcChoices.flatMap(
    ({ accountId: connectionId, region, networkId }) => {
      let partition: InstanceCandidate['partition'] = 'aws';
      if (region.startsWith('cn-')) partition = 'aws-cn';
      if (region.startsWith('us-gov-')) partition = 'aws-us-gov';
      return (resources[networkKey(connectionId, region, networkId)] ?? []).map(
        instance => ({
          ...instance,
          instanceId: instance.id,
          connectionId,
          region,
          networkId,
          partition,
          accountId:
            accounts.find(account => account.id === connectionId)?.accountId ??
            '',
          key: `${connectionId}/${region}/${instance.id}`,
        }),
      );
    },
  );
  const incompatibleSelections = selectedInstances.filter(key => {
    const item = selectedDetails.find(candidate => candidate.key === key);
    if (
      !item ||
      !selectedVpcChoices.some(
        scope =>
          scope.accountId === item.connectionId &&
          scope.region === item.region &&
          scope.networkId === item.networkId,
      )
    )
      return true;
    return (
      allResourcesLoaded && !candidates.some(candidate => candidate.key === key)
    );
  });
  const updateSelection = (keys: string[]) => {
    setSelectedInstances(keys);
    setSelectedDetails([
      ...new Map(
        [...selectedDetails, ...candidates]
          .filter(item => keys.includes(item.key))
          .map(item => [item.key, item]),
      ).values(),
    ]);
    setReview(false);
    idempotencyKey.current = newIdempotencyKey();
  };
  const freeze = async () => {
    if (
      !allAccountsConnected ||
      incompatibleSelections.length > 0 ||
      !allResourcesLoaded ||
      selectedInstances.length < 1 ||
      selectedInstances.length > 200
    )
      return;
    const version = requestVersion.current;
    const connections = selectedAccounts
      .map(connectionId => ({
        connectionId,
        regions: selectedRegionIds(connectionId)
          .map(region => ({
            region,
            networkIds:
              selectedNetworks[accountRegionKey(connectionId, region)] ?? [],
          }))
          .filter(item => item.networkIds.length > 0),
      }))
      .filter(item => item.regions.length > 0);
    setBusy('freeze');
    clearError('freeze');
    try {
      const response = await request('/scopes', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'idempotency-key': idempotencyKey.current,
        },
        body: JSON.stringify({
          schemaVersion: '1.0',
          ...(supersedesScopeId.current
            ? { supersedesScopeId: supersedesScopeId.current }
            : {}),
          connections,
          selectedInstances: candidates
            .filter(item => selectedInstances.includes(item.key))
            .map(
              ({
                partition,
                accountId,
                connectionId,
                region,
                networkId,
                instanceId,
              }) => ({
                partition,
                accountId,
                connectionId,
                region,
                networkId,
                instanceId,
              }),
            ),
        }),
      });
      const scope = parseScope(response);
      if (version !== requestVersion.current) return;
      setFrozen(scope);
      setConfirmed(false);
    } catch (caught) {
      if (version === requestVersion.current)
        setError('freeze', errorMessage(caught));
    } finally {
      if (version === requestVersion.current) setBusy('');
    }
  };

  const restoringScope = value.startsWith('asscope-') && !frozen;
  const locked = Boolean(disabled || busy || restoringScope);
  const discoveryPath = (path: string) => {
    const [connectionId, ...segments] = path.split('/');
    return [
      accounts.find(account => account.id === connectionId)?.accountId ??
        'Unavailable account',
      ...segments,
    ].join('/');
  };

  return (
    <Box mt={2}>
      <TextField
        id={`${fieldId}-provider`}
        select
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
        fullWidth
        label="Cloud provider"
        value={provider}
        onChange={event => void chooseProvider(event.target.value)}
        disabled={locked || Boolean(frozen)}
        helperText="Choose a provider. AWS requires configured customer workload identity; other connectors are coming soon."
        margin="normal"
        variant="outlined"
      >
        <option value="">Select cloud provider</option>
        {providers.map(item => (
          <option
            key={item.id}
            value={item.id}
            disabled={item.availability !== 'available'}
          >
            {item.title}
            {item.availability === 'coming-soon' ? ' — Coming soon' : ''}
            {item.availability === 'not-configured' ? ' — Not configured' : ''}
          </option>
        ))}
      </TextField>
      {errors.providers && (
        <Typography color="error">{errors.providers}</Typography>
      )}
      {errors.restore && (
        <Box>
          <Typography color="error">{errors.restore}</Typography>
          <Button
            disabled={Boolean(busy)}
            onClick={() => setRestoreAttempt(previous => previous + 1)}
          >
            Retry frozen scope
          </Button>
          <Button disabled={Boolean(busy)} onClick={() => invalidate()}>
            Clear unavailable scope
          </Button>
        </Box>
      )}
      {restoringScope && !errors.restore && !busy && (
        <Typography variant="body2" role="status">
          Restoring frozen assessment scope…
        </Typography>
      )}
      {busy && (
        <Box display="flex" alignItems="center" gridGap={8} role="status">
          <CircularProgress size={18} />
          <Typography variant="body2">
            {busy === 'initial' && 'Loading cloud providers and accounts…'}
            {busy === 'detect' && 'Detecting EKS workload identity…'}
            {busy === 'restore' && 'Restoring frozen assessment scope…'}
            {busy === 'manage' && 'Updating cloud account connection…'}
            {busy.startsWith('regions:') && 'Discovering enabled Regions…'}
            {busy === 'networks' && 'Discovering VPCs…'}
            {busy === 'resources' && 'Discovering resources…'}
            {busy === 'freeze' && 'Freezing scope on the server…'}
          </Typography>
        </Box>
      )}

      {frozen && (
        <Box mt={2} p={2} border={1} borderColor="divider" borderRadius={4}>
          <Typography variant="h6">Frozen assessment scope</Typography>
          <Typography variant="body2">
            Scope ID: <strong>{frozen.scopeId}</strong>
          </Typography>
          <Typography variant="body2">
            Server-frozen totals: {frozen.hierarchy.length} accounts ·{' '}
            {
              new Set(
                frozen.hierarchy.flatMap(account =>
                  account.regions.map(region => region.region),
                ),
              ).size
            }{' '}
            distinct Regions ·{' '}
            {frozen.hierarchy.reduce(
              (count, account) =>
                count +
                account.regions.reduce(
                  (regionCount, region) => regionCount + region.networks.length,
                  0,
                ),
              0,
            )}{' '}
            VPCs · {frozen.resourceCount} resources
          </Typography>
          {!confirmed && (
            <Typography variant="body2">
              Preview found {resourceCount} resources. Review the server-frozen
              hierarchy and confirm before this scope is used in the assessment.
            </Typography>
          )}
          {frozen.hierarchy.map(account => (
            <details key={account.connectionId}>
              <summary>
                {account.accountId}
                {' — '}
                {account.regions.length} Regions
              </summary>
              <Box pl={2}>
                {account.regions.map(region => (
                  <details key={region.region}>
                    <summary>
                      {region.region} — {region.networks.length} VPCs
                    </summary>
                    <Box pl={2}>
                      {region.networks.map(network => (
                        <details key={network.networkId}>
                          <summary>
                            {network.name ?? network.networkId} (
                            {network.networkId}){' — '}
                            {network.subnets.length} subnets
                          </summary>
                          <Box pl={2}>
                            {network.subnets.map(subnet => (
                              <details key={subnet.subnetId ?? 'no-subnet'}>
                                <summary>
                                  {subnet.subnetId ?? 'No subnet'} —{' '}
                                  {subnet.resourceRefs.length} resources
                                </summary>
                                <ul>
                                  {subnet.resourceRefs.map(resourceRef => (
                                    <li key={resourceRef}>
                                      {frozen.children?.find(
                                        child =>
                                          child.resourceRef === resourceRef,
                                      )?.displayName ?? resourceRef}
                                      {' ('}
                                      {resourceRef})
                                    </li>
                                  ))}
                                </ul>
                              </details>
                            ))}
                          </Box>
                        </details>
                      ))}
                    </Box>
                  </details>
                ))}
              </Box>
            </details>
          ))}
          {!confirmed && !disabled && (
            <Button
              variant="contained"
              color="primary"
              disabled={locked}
              onClick={() => {
                setConfirmed(true);
                onChange(frozen.scopeId);
              }}
            >
              Confirm scope and continue
            </Button>
          )}
          {!disabled && (
            <Button
              disabled={locked}
              onClick={() => {
                invalidate();
                setSelectedAccounts([]);
                setNetworks({});
                setSelectedNetworks({});
              }}
            >
              Change selection
            </Button>
          )}
        </Box>
      )}

      {provider === 'aws' && !frozen && !restoringScope && (
        <>
          <Box mt={2}>
            <Typography variant="h6">AWS accounts</Typography>
            <Typography variant="body2">
              Select connected accounts. Runtime identity detection runs once
              when AWS is opened.
            </Typography>
            {errors.detect && (
              <Box>
                <Typography color="error">
                  Runtime detection: {errors.detect}
                </Typography>
                <Button disabled={locked} onClick={() => void detectRuntime()}>
                  Retry runtime detection
                </Button>
              </Box>
            )}
            {errors.accounts && (
              <Box>
                <Typography color="error">{errors.accounts}</Typography>
                <Button
                  disabled={locked}
                  onClick={() =>
                    void loadAccounts().catch(caught =>
                      setError('accounts', errorMessage(caught)),
                    )
                  }
                >
                  Retry accounts
                </Button>
              </Box>
            )}
            {accounts.length === 0 && !busy && !errors.accounts && (
              <Typography variant="body2">
                No AWS accounts are connected yet.
              </Typography>
            )}
            <AwsAccountIdInput
              request={request}
              canManage={canManage}
              disabled={locked}
              onAdd={accountId =>
                manageAccount('/aws/accounts/connect', 'POST', { accountId })
              }
            />
            {accounts.map(account => (
              <Box
                key={account.id}
                mt={1}
                p={1}
                border={1}
                borderColor="divider"
                borderRadius={4}
              >
                <SelectionCheck
                  label={`${account.accountId} — ${account.status}`}
                  checked={selectedAccounts.includes(account.id)}
                  disabled={locked || account.status !== 'CONNECTED'}
                  onChange={checked => void toggleAccount(account.id, checked)}
                />
                <Typography variant="body2">
                  Connection:{' '}
                  {account.kind === 'runtime'
                    ? 'EKS Workload Identity'
                    : 'AssumeRole'}{' '}
                  · AWS account {account.accountId}
                </Typography>
                {account.error && (
                  <Typography color="error" variant="body2">
                    {account.error.message}
                    {account.error.permission
                      ? ` (${account.error.permission})`
                      : ''}
                  </Typography>
                )}
                <details>
                  <summary>
                    Permission checks ({account.permissionChecks?.length ?? 0})
                    {account.permissionChecks?.some(
                      check => check.status === 'FAIL',
                    )
                      ? ' · attention required'
                      : ''}
                  </summary>
                  {account.permissionChecks?.length ? (
                    <ul>
                      {account.permissionChecks.map(check => (
                        <li key={check.permission}>
                          {check.permission}: {check.status}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <Typography variant="body2">
                      No permission checks reported.
                    </Typography>
                  )}
                </details>
                {canManage && account.canManage && !disabled && (
                  <Box display="flex" flexWrap="wrap" gridGap={8} mt={1}>
                    <Button
                      disabled={locked}
                      size="small"
                      onClick={() =>
                        void manageAccount(
                          `/accounts/${encodeURIComponent(
                            account.id,
                          )}/validate`,
                          'POST',
                        )
                      }
                    >
                      {account.status === 'CONNECTED'
                        ? 'Revalidate'
                        : 'Retry validation'}
                    </Button>
                    <Button
                      disabled={locked}
                      size="small"
                      onClick={() =>
                        void manageAccount(
                          `/accounts/${encodeURIComponent(account.id)}`,
                          'DELETE',
                        )
                      }
                    >
                      Remove
                    </Button>
                  </Box>
                )}
              </Box>
            ))}
            {errors.manage && (
              <Typography color="error">{errors.manage}</Typography>
            )}
          </Box>

          {selectedAccounts.map(accountId => {
            const account = accounts.find(item => item.id === accountId);
            const choices = regions[accountId];
            const mode = regionModes[accountId] ?? 'all';
            return (
              <Box
                key={accountId}
                mt={2}
                p={2}
                border={1}
                borderColor="divider"
                borderRadius={4}
              >
                <Typography variant="h6">
                  Regions · {account?.accountId ?? 'Unavailable account'}
                </Typography>
                {errors[`regions:${accountId}`] && (
                  <Box>
                    <Typography color="error">
                      {errors[`regions:${accountId}`]}
                    </Typography>
                    <Button
                      disabled={locked}
                      onClick={() => void toggleAccount(accountId, true)}
                    >
                      Retry Regions
                    </Button>
                  </Box>
                )}
                {choices && choices.length === 0 && (
                  <Typography>
                    No enabled Regions are available for this account.
                  </Typography>
                )}
                {choices && choices.length > 0 && (
                  <>
                    <TextField
                      id={`${fieldId}-region-mode-${accountId}`}
                      select
                      SelectProps={{ native: true }}
                      InputLabelProps={{ shrink: true }}
                      label={`Region scope for ${
                        account?.accountId ?? 'Unavailable account'
                      }`}
                      value={mode}
                      onChange={event => {
                        invalidate();
                        setRegionModes(previous => ({
                          ...previous,
                          [accountId]: event.target.value as 'all' | 'selected',
                        }));
                        setNetworks({});
                        setSelectedNetworks({});
                      }}
                      disabled={locked}
                      margin="normal"
                      variant="outlined"
                    >
                      <option value="all">All enabled Regions</option>
                      <option value="selected">Selected Regions</option>
                    </TextField>
                    {mode === 'all' && (
                      <Typography variant="body2">
                        Enabled Regions:{' '}
                        {choices.map(region => region.name).join(', ')}
                      </Typography>
                    )}
                    {mode === 'selected' && (
                      <Box display="flex" flexWrap="wrap" gridGap={12} mt={1}>
                        {choices.map(region => (
                          <SelectionCheck
                            key={region.id}
                            label={region.name}
                            checked={(
                              selectedRegions[accountId] ?? []
                            ).includes(region.id)}
                            disabled={locked}
                            onChange={checked => {
                              invalidate();
                              setSelectedRegions(previous => ({
                                ...previous,
                                [accountId]: checked
                                  ? [...(previous[accountId] ?? []), region.id]
                                  : (previous[accountId] ?? []).filter(
                                      id => id !== region.id,
                                    ),
                              }));
                              setNetworks({});
                              setSelectedNetworks({});
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </>
                )}
              </Box>
            );
          })}

          {selectedAccounts.length > 0 && (
            <Box mt={2}>
              <Button
                variant="outlined"
                disabled={
                  locked || !allAccountsConnected || selectedPairs.length === 0
                }
                onClick={() => void discoverNetworks()}
              >
                Discover VPCs
              </Button>
              {errors.networks && (
                <Typography color="error">{errors.networks}</Typography>
              )}
            </Box>
          )}

          {Object.keys(networks).length > 0 && (
            <Box mt={2}>
              <Typography variant="h6">VPCs</Typography>
              <TextField
                id={`${fieldId}-network-search`}
                label="Search VPCs"
                value={networkSearch}
                onChange={event => setNetworkSearch(event.target.value)}
                margin="normal"
                fullWidth
              />
              {selectedPairs.map(({ accountId, region }) => {
                const pair = accountRegionKey(accountId, region);
                const choices = networks[pair];
                if (choices === undefined) return null;
                const filtered = choices.filter(network =>
                  `${network.name ?? ''} ${network.id}`
                    .toLowerCase()
                    .includes(networkSearch.toLowerCase()),
                );
                return (
                  <Box
                    key={pair}
                    mt={1}
                    p={2}
                    border={1}
                    borderColor="divider"
                    borderRadius={4}
                  >
                    <Typography variant="subtitle1">
                      {accounts.find(item => item.id === accountId)
                        ?.accountId ?? 'Unavailable account'}{' '}
                      · {region}
                    </Typography>
                    {choices.length === 0 && (
                      <Typography>No VPCs found in this Region.</Typography>
                    )}
                    {filtered.length === 0 && choices.length > 0 && (
                      <Typography>No VPCs match this search.</Typography>
                    )}
                    {choices.length > 0 && (
                      <Box display="flex" gridGap={8}>
                        <Button
                          size="small"
                          disabled={locked}
                          onClick={() => {
                            invalidate();
                            setSelectedNetworks(previous => ({
                              ...previous,
                              [pair]: choices.map(item => item.id),
                            }));
                          }}
                        >
                          Select all VPCs
                        </Button>
                        <Button
                          size="small"
                          disabled={locked}
                          onClick={() => {
                            invalidate();
                            setSelectedNetworks(previous => ({
                              ...previous,
                              [pair]: [],
                            }));
                          }}
                        >
                          Clear VPCs
                        </Button>
                      </Box>
                    )}
                    {filtered.map(network => (
                      <Box key={network.id} mt={1}>
                        <SelectionCheck
                          label={`${network.name ?? network.id} (${
                            network.id
                          })${network.isDefault ? ' · default' : ''}`}
                          checked={(selectedNetworks[pair] ?? []).includes(
                            network.id,
                          )}
                          disabled={locked}
                          onChange={checked => {
                            invalidate();
                            setSelectedNetworks(previous => ({
                              ...previous,
                              [pair]: checked
                                ? [...(previous[pair] ?? []), network.id]
                                : (previous[pair] ?? []).filter(
                                    id => id !== network.id,
                                  ),
                            }));
                          }}
                        />
                        <Typography variant="caption" display="block">
                          {network.subnets.length} subnets
                          {network.cidr ? ` · ${network.cidr}` : ''}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                );
              })}
            </Box>
          )}
          {Object.entries(errors)
            .filter(([key]) => key.startsWith('network:'))
            .map(([key, message]) => (
              <Typography key={key} color="error">
                {discoveryPath(key.slice(8))}: {message}
              </Typography>
            ))}

          {allNetworksLoaded && selectedVpcChoices.length > 0 && (
            <Box mt={2}>
              <TextField
                id={`${fieldId}-operating-system`}
                select
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                label="Operating system"
                value={osView}
                disabled={Boolean(disabled || (busy && busy !== 'resources'))}
                onChange={event => {
                  setOsView(event.target.value);
                  void discoverResources();
                }}
                helperText="OS views refresh only this account/Region/VPC inventory. They never select servers or execute guest commands."
                fullWidth
                margin="normal"
              >
                <option value="">All inventory — choose an OS</option>
                <option value="LINUX">Linux</option>
                <option value="WINDOWS">Windows</option>
                <option value="UNVERIFIED">
                  OS not verified (
                  {
                    candidates.filter(item => osMatches(item, 'UNVERIFIED'))
                      .length
                  }
                  )
                </option>
                <option value="UNSUPPORTED">
                  Unsupported OS families (
                  {
                    candidates.filter(item => osMatches(item, 'UNSUPPORTED'))
                      .length
                  }
                  )
                </option>
              </TextField>
              <Button
                variant="outlined"
                disabled={locked}
                onClick={() => void discoverResources()}
              >
                Discover resources
              </Button>
            </Box>
          )}
          {Object.entries(errors)
            .filter(([key]) => key.startsWith('resource:'))
            .map(([key, message]) => (
              <Typography key={key} color="error">
                {discoveryPath(key.slice(9))}: {message}
              </Typography>
            ))}
          {(allResourcesLoaded ||
            resourceCount > 0 ||
            selectedInstances.length > 0) && (
            <Box mt={2}>
              {selectionNotice && (
                <Typography role="status">{selectionNotice}</Typography>
              )}
              <InstanceSelectionTable
                candidates={candidates}
                selectedCandidates={[
                  ...new Map(
                    [...selectedDetails, ...candidates].map(item => [
                      item.key,
                      item,
                    ]),
                  ).values(),
                ]}
                osView={osView}
                selected={selectedInstances}
                onChange={updateSelection}
                complete={allResourcesLoaded}
                disabled={locked}
              />
              {incompatibleSelections.length > 0 && (
                <Box role="alert">
                  <Typography>
                    {incompatibleSelections.length} selected instances are
                    outside the current network scope or no longer in refreshed
                    inventory. Restore their scope, or explicitly remove them
                    before freeze.
                  </Typography>
                  <Button
                    disabled={locked}
                    onClick={() =>
                      updateSelection(
                        selectedInstances.filter(
                          key => !incompatibleSelections.includes(key),
                        ),
                      )
                    }
                  >
                    Remove incompatible selections (
                    {incompatibleSelections.length})
                  </Button>
                </Box>
              )}
              <Typography variant="h6">Review assessment scope</Typography>
              {selectedVpcChoices.map(({ accountId, region, networkId }) => {
                const found =
                  resources[networkKey(accountId, region, networkId)] ?? [];
                return (
                  <Box key={networkKey(accountId, region, networkId)} mt={1}>
                    <Typography variant="body2">
                      {accounts.find(item => item.id === accountId)
                        ?.accountId ?? 'Unavailable account'}{' '}
                      / {region} / {networkId}: {found.length} resources
                    </Typography>
                  </Box>
                );
              })}
              {resourceCount === 0 && (
                <Typography color="error">
                  No eligible resources were found in the selected VPCs.
                </Typography>
              )}
              {!review &&
                selectedInstances.length > 0 &&
                selectedInstances.length <= 200 && (
                  <Button
                    disabled={
                      locked ||
                      !allAccountsConnected ||
                      !allResourcesLoaded ||
                      incompatibleSelections.length > 0
                    }
                    onClick={() => setReview(true)}
                  >
                    Review and freeze scope
                  </Button>
                )}
              {review && (
                <Box mt={2}>
                  <Typography variant="body2">
                    Horizon will revalidate and freeze exactly{' '}
                    {selectedInstances.length} explicitly selected instances.
                    Other candidates and dependencies will not become assessment
                    roots or guest-command targets. No cloud changes are made.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={
                      locked ||
                      !allAccountsConnected ||
                      !allResourcesLoaded ||
                      incompatibleSelections.length > 0 ||
                      selectedInstances.length < 1 ||
                      selectedInstances.length > 200
                    }
                    onClick={() => void freeze()}
                  >
                    Freeze assessment scope
                  </Button>
                  {errors.freeze && (
                    <Typography color="error">{errors.freeze}</Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
